import { Component, OnInit } from '@angular/core';
import { JobService } from '../services/job.service';
import { UserService } from '../../users/services/user.service';
import { Job } from '../models/job.model';
import { JobStatus } from '../models/job-status.enum';
import { Worker } from '../../users/models/worker.model';

@Component({
  selector: 'app-jobs-list',
  templateUrl: './jobs-list.component.html',
  styleUrls: ['./jobs-list.component.css']
})
export class JobsListComponent implements OnInit {

  jobs: Job[]           = [];
  filteredJobs: Job[]   = [];
  pagedJobs: Job[]      = [];
  workers: Worker[]     = [];
  loading               = true;

  // Filters
  searchTerm      = '';
  selectedStatus: JobStatus | '' = '';
  selectedWorker  = '';

  // Expose enum to template
  JobStatus = JobStatus;

  // Pagination
  currentPage  = 1;
  pageSize     = 10;
  totalPages   = 1;
  pageNumbers: number[] = [];

  // Delete confirmation modal
  confirmDeleteId: string | null = null;

  // Expose Math for template
  Math = Math;

  constructor(
    private jobService: JobService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.loadWorkers();
    this.loadJobs();
  }

  loadWorkers(): void {
    this.userService.getWorkers().subscribe(w => (this.workers = w));
  }

  loadJobs(): void {
    this.loading = true;
    this.jobService.getJobs().subscribe({
      next: data => {
        this.jobs         = data;
        this.filteredJobs = data;
        this.loading      = false;
        this.currentPage  = 1;
        this.buildPagination();
        this.updatePagedJobs();
      },
      error: () => (this.loading = false)
    });
  }

  // ── Filters ──────────────────────────────────────────────

  applyFilters(): void {
    this.filteredJobs = this.jobs.filter(job => {
      const matchesSearch =
        !this.searchTerm ||
        job.title.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        job.locationName.toLowerCase().includes(this.searchTerm.toLowerCase());

      const matchesStatus =
        this.selectedStatus === '' || job.status === this.selectedStatus;

      const matchesWorker =
        !this.selectedWorker || job.assignedUserId === this.selectedWorker;

      return matchesSearch && matchesStatus && matchesWorker;
    });

    this.currentPage = 1;
    this.buildPagination();
    this.updatePagedJobs();
  }

  resetFilters(): void {
    this.searchTerm     = '';
    this.selectedStatus = '';
    this.selectedWorker = '';
    this.applyFilters();
  }

  // ── Pagination ────────────────────────────────────────────

  buildPagination(): void {
    this.totalPages  = Math.max(1, Math.ceil(this.filteredJobs.length / this.pageSize));
    this.pageNumbers = Array.from({ length: this.totalPages }, (_, i) => i + 1)
      .filter(p => Math.abs(p - this.currentPage) <= 2);
  }

  updatePagedJobs(): void {
    const start      = (this.currentPage - 1) * this.pageSize;
    this.pagedJobs   = this.filteredJobs.slice(start, start + this.pageSize);
    this.buildPagination();
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.updatePagedJobs();
  }

  // ── Summary ───────────────────────────────────────────────

  countByStatus(status: JobStatus): number {
    return this.jobs.filter(j => j.status === status).length;
  }

  // ── Job actions ───────────────────────────────────────────

  updateStatus(job: Job, status: JobStatus): void {
    this.jobService.updateJobStatus(job.id, { status }).subscribe(() => {
      job.status = status;
      this.applyFilters();
    });
  }

  deleteJob(id: string): void {
    this.confirmDeleteId = id;
  }

  confirmDelete(): void {
    if (!this.confirmDeleteId) return;
    this.jobService.deleteJob(this.confirmDeleteId).subscribe(() => {
      this.confirmDeleteId = null;
      this.loadJobs();
    });
  }

  assignWorker(job: Job): void {
    if (!job.assignedUserId) return;
    this.jobService.assignWorker(job.id, job.assignedUserId).subscribe(() => {
      this.applyFilters();
    });
  }

  // ── Helpers ───────────────────────────────────────────────

  getStatusLabel(status: JobStatus): string {
    const labels: Record<JobStatus, string> = {
      [JobStatus.Pending]:    'Pending',
      [JobStatus.InProgress]: 'In Progress',
      [JobStatus.Completed]:  'Completed',
      [JobStatus.Failed]:     'Failed',
    };
    return labels[status] ?? 'Unknown';
  }

  getWorkerName(userId?: string | null): string {
    if (!userId) return 'Unassigned';
    return this.workers.find(w => w.id === userId)?.fullName ?? 'Unknown';
  }

  getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : name.slice(0, 2).toUpperCase();
  }
}